const BASE32_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
const TOTP_PERIOD_SECONDS = 30;

function decodeBase32(value: string): ArrayBuffer {
  const normalized = value.trim().toUpperCase().replace(/\s+/g, '').replace(/=+$/, '');

  if (!normalized || normalized.length > 128 || !/^[A-Z2-7]+$/.test(normalized)) {
    throw new Error('invalid_base32');
  }

  const bytes: number[] = [];
  let buffer = 0;
  let bitsLeft = 0;

  for (const character of normalized) {
    buffer = (buffer << 5) | BASE32_ALPHABET.indexOf(character);
    bitsLeft += 5;

    if (bitsLeft >= 8) {
      bitsLeft -= 8;
      bytes.push((buffer >>> bitsLeft) & 0xff);
      buffer &= (1 << bitsLeft) - 1;
    }
  }

  if (bytes.length === 0 || buffer !== 0) {
    throw new Error('invalid_base32');
  }

  const decoded = new Uint8Array(new ArrayBuffer(bytes.length));
  decoded.set(bytes);
  return decoded.buffer;
}

export function totpSecondsRemaining(timestampMs = Date.now()): number {
  const elapsed = Math.floor(timestampMs / 1000) % TOTP_PERIOD_SECONDS;
  return TOTP_PERIOD_SECONDS - elapsed;
}

export async function generateTotpCode(
  secretBase32: string,
  timestampMs = Date.now(),
): Promise<string> {
  if (!globalThis.crypto?.subtle) {
    throw new Error('web_crypto_unavailable');
  }

  const secret = decodeBase32(secretBase32);
  const counter = BigInt(Math.floor(timestampMs / 1000 / TOTP_PERIOD_SECONDS));
  const counterBytes = new ArrayBuffer(8);
  new DataView(counterBytes).setBigUint64(0, counter, false);

  const key = await globalThis.crypto.subtle.importKey(
    'raw',
    secret,
    { name: 'HMAC', hash: 'SHA-1' },
    false,
    ['sign'],
  );
  const digest = new Uint8Array(
    await globalThis.crypto.subtle.sign('HMAC', key, counterBytes),
  );
  if (digest.byteLength !== 20) {
    throw new Error('invalid_hmac_digest');
  }

  const view = new DataView(digest.buffer, digest.byteOffset, digest.byteLength);
  const offset = view.getUint8(digest.byteLength - 1) & 0x0f;
  const binary = view.getUint32(offset, false) & 0x7fffffff;

  return String(binary % 1_000_000).padStart(6, '0');
}
